import { useState, useEffect } from 'react';
import { Offcanvas, Button, Form, Modal, Alert, Tabs, Tab } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';

const TaxInvoicePreview = ({
    showModal,
    handleClose,
    createdInvoice,
    quotationData,
    customIntegration,
    setCustomIntegration,
    editableDeploymentIntegration,
    setEditableDeploymentIntegration,
    customIntegrationDescription,
    setCustomIntegrationDescription,
    onDownloadPDF,
    onInvoiceUpdate,// Add this prop to refresh the list after upload
    fetchTaxInvoices // Add this prop to refresh the list after upload
}) => {
    console.log("🔍 DEBUG TaxInvoicePreview:");
    console.log("showModal:", showModal);
    console.log("createdInvoice:", createdInvoice);
    console.log("quotationData:", quotationData);

    const [companyDetails, setCompanyDetails] = useState(null);
    const [proformaInvoiceTerms, setProformaInvoiceTerms] = useState('');
    const [bankAccounts, setBankAccounts] = useState([]);

    // Upload states
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [hasProducts, setHasProducts] = useState(false);
    const [documentViewMode, setDocumentViewMode] = useState('preview'); // 'preview' or 'uploaded'

    // Check if quotation has products
    useEffect(() => {
        if (quotationData) {
            const hasProductItems = quotationData?.product_item_details?.length > 0;
            const hasCustomIntegration = customIntegration || editableDeploymentIntegration || customIntegrationDescription;
            setHasProducts(hasProductItems || hasCustomIntegration);
        }
    }, [quotationData, customIntegration, editableDeploymentIntegration, customIntegrationDescription]);
    const hasUploadedDocument = createdInvoice?.tax_invoice_file;

    // Reset view mode when modal opens
    useEffect(() => {
        if (showModal) {
            if (hasUploadedDocument) {
                setDocumentViewMode('uploaded');
            } else {
                setDocumentViewMode('preview');
            }
        }
    }, [showModal, hasUploadedDocument]);

    // Fix: Convert deployment descriptions object to string or array
    const formatDeploymentDescriptions = (descriptions) => {
        if (!descriptions) return '-';

        if (typeof descriptions === 'string') {
            try {
                descriptions = JSON.parse(descriptions);
            } catch (e) {
                return descriptions; // Return as-is if it's already a string
            }
        }

        if (typeof descriptions === 'object') {
            // If it's an object with keys like {136: "Integration with voice agents"}
            if (Object.keys(descriptions).length > 0) {
                return Object.values(descriptions).join(', ');
            }
            return '-';
        }

        return descriptions || '-';
    };

    // Fix: Parse custom integration description HTML safely
    const parseCustomIntegrationDescription = (description) => {
        if (!description) return '-';

        if (typeof description === 'string' && description.startsWith('<')) {
            // Remove HTML tags and convert to plain text
            return description.replace(/<[^>]*>/g, '');
        }

        return description;
    };

    useEffect(() => {
        if (showModal && createdInvoice) {
            const clientCurrency = quotationData?.project?.client?.currency || 'AED';
            fetchCompanyDetails();
            fetchPITerms(clientCurrency);
            fetchBankAccounts();
        }
    }, [showModal, createdInvoice]);

    const fetchPITerms = async (currency) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/quotationterm`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            const term = response.data.quotation_terms.data.find(term => term.type === "proforma_invoice");
            let formattedTerms = "";

            if (term) {
                if (currency === 'AED') {
                    formattedTerms = term.value_local || term.value || "";
                    console.log('📝 Using UAE Local Client Terms (AED) for Proforma Invoice');
                } else if (currency === 'USD') {
                    formattedTerms = term.value_export || term.value || "";
                    console.log('📝 Using Export Client Terms (USD) for Proforma Invoice');
                } else {
                    formattedTerms = term.value || "";
                    console.log('📝 Using Default Terms for Proforma Invoice');
                }

                formattedTerms = formattedTerms.replace(/{{currency}}/g, currency || "USD");
            }
            setProformaInvoiceTerms(formattedTerms);

        } catch (error) {
            console.error("Error fetching quotation terms:", error);
        }
    };

    const fetchCompanyDetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/settings`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setCompanyDetails(response.data.setting);
        } catch (error) {
            console.error("Error fetching company details:", error);
        }
    };

    const fetchBankAccounts = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/bank-accounts?type=bank account`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                },
                skipLoader: true,
            });

            const firstTwoAccounts = (response.data.account || []).slice(0, 2);
            setBankAccounts(firstTwoAccounts);
        } catch (error) {
            console.error("Error fetching bank accounts:", error);
            setBankAccounts([]);
        }
    };

    // Upload handlers
    const handleShowUploadModal = () => {
        setShowUploadModal(true);
        setUploadFile(null);
        setUploadError('');
    };

    const handleCloseUploadModal = () => {
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadError('');
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(file.type)) {
                setUploadError('Please upload PDF, JPEG, PNG, or Word documents only');
                return;
            }

            // Validate file size (5MB)
            if (file.size > 5 * 1024 * 1024) {
                setUploadError('File size must be less than 5MB');
                return;
            }

            setUploadFile(file);
            setUploadError('');
        }
    };


    const handleUpload = async () => {
        console.log('🚀 handleUpload STARTED');

        if (!uploadFile) {
            console.log('❌ No upload file selected');
            setUploadError('Please select a file to upload');
            return;
        }

        try {
            console.log('🔄 Starting upload process...');
            setUploading(true);
            setUploadError(''); // Clear previous errors

            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            if (!backendUrl || !userInfo || !token) {
                throw new Error('Missing required configuration');
            }

            console.log('📁 Preparing form data...');
            const formData = new FormData();
            formData.append("tax_invoice_file", uploadFile);
            console.log('📄 File to upload:', uploadFile.name, uploadFile.size);

            console.log('🌐 Making upload API call to:', `${backendUrl}/api/taxinvoices/${createdInvoice.id}/upload`);

            const response = await axios.post(
                `${backendUrl}/api/taxinvoices/${createdInvoice.id}/upload`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Tenant-Origin": origin,
                    },
                    timeout: 30000, // 30 second timeout
                }
            );

            console.log('✅ Upload successful - Response:', response.data);

            // Show success message FIRST
            console.log('🎉 Showing success toast...');

            console.log('✅ Success toast shown');

            // Call refresh APIs
            console.log('🔄 Calling refresh APIs...');

            if (onInvoiceUpdate) {
                console.log('📞 Calling onInvoiceUpdate...');
                await onInvoiceUpdate();
                console.log('✅ onInvoiceUpdate completed');
            } else {
                console.log('❌ onInvoiceUpdate not available');
            }

            if (fetchTaxInvoices) {
                console.log('📞 Calling fetchTaxInvoices...');
                await fetchTaxInvoices();
                console.log('✅ fetchTaxInvoices completed');
            } else {
                console.log('❌ fetchTaxInvoices not available');
            }

            // FINALLY close the modal
            console.log('🔒 Closing upload modal...');
            handleCloseUploadModal();
            console.log('🎉 Upload process COMPLETED SUCCESSFULLY');

        } catch (error) {
            console.error('❌ UPLOAD ERROR:', error);
            console.error('❌ Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                code: error.code
            });

            let errorMessage = "Upload failed";
            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }

            setUploadError(errorMessage);

            // Show error toast
            Swal.fire({
                toast: true,
                position: "top-end",
                icon: "error",
                title: errorMessage,
                timer: 5000,
                showConfirmButton: false,
            });
        } finally {
            console.log('🔚 Finally block - setting uploading to false');
            setUploading(false);
        }
    };
    // Check if tax invoice has uploaded document

    // Get file URL for iframe
    // Get file URL for iframe - FIXED VERSION
    const getFileUrl = () => {
        if (!hasUploadedDocument) return null;
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        // Log the file path for debugging
        console.log('📁 File path from database:', createdInvoice.tax_invoice_file);

        // Handle different file path formats
        let filePath = createdInvoice.tax_invoice_file;

        // Remove any leading slashes or storage/ prefixes if they exist
        if (filePath.startsWith('/')) {
            filePath = filePath.substring(1);
        }
        if (filePath.startsWith('storage/')) {
            filePath = filePath.substring(8); // Remove 'storage/' prefix
        }

        const fullUrl = `${backendUrl}/storage/${filePath}`;
        console.log('🔗 Generated file URL:', fullUrl);

        return fullUrl;
    };

    // Check if file is PDF
    const isPdfFile = () => {
        if (!hasUploadedDocument) return false;
        return createdInvoice.tax_invoice_file.toLowerCase().endsWith('.pdf');
    };

    // Check if file is image
    const isImageFile = () => {
        if (!hasUploadedDocument) return false;
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp'];
        return imageExtensions.some(ext =>
            createdInvoice.tax_invoice_file.toLowerCase().endsWith(ext)
        );
    };

    if (!showModal || !createdInvoice) {
        console.log("❌ Preview modal not rendering because:", {
            showModal,
            hasCreatedInvoice: !!createdInvoice
        });
        return null;
    }

    const clientCurrency = quotationData?.project?.client?.currency || 'AED';
    const vatRate = clientCurrency === 'AED' ? 0.05 : 0;

    // Calculate amounts similar to PDF
    const basicAmountValue = Number(createdInvoice.total_amount || createdInvoice.basic_amount || createdInvoice.amount_without_vat || 0);
    const vatAmountValue = Number(createdInvoice.vat_amount || createdInvoice.vat || 0);
    const discountAmountValue = Number(createdInvoice.discount_amount || 0);
    const afterDiscount = Number(createdInvoice.total_with_vat || (basicAmountValue + vatAmountValue)) - discountAmountValue;

    // Number to words function
    const numberToWords = (number) => {
        const words = [
            'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
            'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
            'seventeen', 'eighteen', 'nineteen'
        ];

        const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

        if (number < 20) return words[number];
        if (number < 100) {
            return tens[Math.floor(number / 10)] + (number % 10 ? '-' + words[number % 10] : '');
        }
        if (number < 1000) {
            return words[Math.floor(number / 100)] + ' hundred' + (number % 100 ? ' and ' + numberToWords(number % 100) : '');
        }
        return number.toString();
    };

    // Generate amount in words
    const generateAmountInWords = (amount) => {
        const integerPart = Math.floor(amount);
        const decimalPart = Math.round((amount - integerPart) * 100);

        let words = numberToWords(integerPart);
        words = words.charAt(0).toUpperCase() + words.slice(1);

        if (decimalPart > 0) {
            words += ' and ' + numberToWords(decimalPart) + ' cents';
        }

        const currencyLabel = clientCurrency === 'AED' ? 'UAE' : 'USD';
        return `${currencyLabel} ${words}${decimalPart > 0 ? ` and ${decimalPart}/100` : ''} only.`;
    };

    // Filter bank accounts by currency
    const matchedBanks = bankAccounts.filter(bank =>
        bank.currency?.currency?.toUpperCase() === clientCurrency?.toUpperCase()
    );

    // Helper function to format deployment descriptions as bullet list
    const formatDeploymentDescriptionsAsList = (descriptions) => {
        if (!descriptions) return null;

        if (typeof descriptions === 'string') {
            try {
                descriptions = JSON.parse(descriptions);
            } catch (e) {
                // If it's already a string with HTML list, return as is
                if (descriptions.includes('<ul>') || descriptions.includes('<li>')) {
                    return <div dangerouslySetInnerHTML={{ __html: descriptions }} />;
                }
                // If it's a plain string, convert to list
                return (
                    <ul style={{ margin: 0, paddingLeft: '15px' }}>
                        <li>{descriptions}</li>
                    </ul>
                );
            }
        }

        if (typeof descriptions === 'object') {
            if (Object.keys(descriptions).length > 0) {
                return (
                    <ul style={{ margin: 0, paddingLeft: '15px' }}>
                        {Object.values(descriptions).map((desc, index) => (
                            <li key={index}>{desc}</li>
                        ))}
                    </ul>
                );
            }
            return null;
        }

        return null;
    };

    // Helper function to parse custom integration description as list
    const parseCustomIntegrationDescriptionAsList = (description) => {
        if (!description) return null;

        if (typeof description === 'string') {
            // If it already contains HTML list tags, render as HTML
            if (description.includes('<ul>') || description.includes('<li>') || description.includes('<ol>')) {
                return <div dangerouslySetInnerHTML={{ __html: description }} />;
            }
            // If it's a plain string, assume each line is a list item
            const lines = description.split('\n').filter(line => line.trim());
            if (lines.length > 1) {
                return (
                    <ul style={{ margin: 0, paddingLeft: '15px' }}>
                        {lines.map((line, index) => (
                            <li key={index}>{line.trim()}</li>
                        ))}
                    </ul>
                );
            } else {
                // Single line - show as regular paragraph
                return <div>{description}</div>;
            }
        }

        return <div>{description}</div>;
    };

    return (
        <>
            <Offcanvas show={showModal} onHide={handleClose} placement="end" style={{ width: '950px' }}>
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title className="fw-bold">Tax Invoice Preview</Offcanvas.Title>
                    <div className="position-absolute top-0 end-0 p-3 d-flex gap-2 me-4">
                        <Button
                            variant="outline-primary"
                            onClick={handleShowUploadModal}
                            size="sm"
                            title={hasUploadedDocument ? "Edit/Replace Document" : "Upload Tax Invoice"}
                        >
                            <i className={`fa ${hasUploadedDocument ? 'fa-edit' : 'fa-upload'} me-1`}></i>
                            {hasUploadedDocument ? "Update" : "Upload"}
                        </Button>
                    </div>
                </Offcanvas.Header>

                <Offcanvas.Body className="p-4" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                    <div className="preview-container" style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>

                        <Tabs
                            activeKey={documentViewMode}
                            onSelect={(k) => setDocumentViewMode(k)}
                            className="mb-4 custom-preview-tabs"
                        >
                            <Tab eventKey="preview" title={<span><i className="fa fa-file-invoice me-1"></i> System Generated</span>}>
                                <div className="d-flex justify-content-end mb-3">
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        onClick={() => onDownloadPDF(createdInvoice.id, createdInvoice.ref_no)}
                                    >
                                        <i className="fa fa-download me-1"></i> Download Generated PDF
                                    </Button>
                                </div>
                                <div className="system-preview-content p-3 border rounded shadow-sm bg-white">
                                    {documentViewMode === 'preview' && (
                                        <>
                                            {hasProducts ? (
                                                <>
                                                    {/* Invoice Details */}
                                                    <div className="details-container" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', width: '100%' }}>
                                                        <div className="left-column" style={{ float: 'left', width: '48%' }}>
                                                            <div className="project-info">
                                                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                                                    <strong className="detail-label" style={{ display: 'inline-block', width: '100px', fontWeight: 'bold' }}>Project:</strong>
                                                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                                                        {quotationData?.project?.name || 'N/A'}
                                                                    </span>
                                                                </div>
                                                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                                                    <strong className="detail-label" style={{ display: 'inline-block', width: '100px', fontWeight: 'bold' }}>Document No:</strong>
                                                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                                                        {createdInvoice.ref_no || 'N/A'}
                                                                    </span>
                                                                </div>
                                                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                                                    <strong className="detail-label" style={{ display: 'inline-block', width: '100px', fontWeight: 'bold' }}>Date:</strong>
                                                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                                                        {new Date(createdInvoice.created_at).toLocaleDateString('en-GB')}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="right-column" style={{ float: 'right', width: '48%' }}>
                                                            <div className="customer-info">
                                                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                                                    <strong className="detail-label" style={{ display: 'inline-block', width: '100px', fontWeight: 'bold' }}>Customer:</strong>
                                                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                                                        {quotationData?.project?.client?.name || ''}
                                                                    </span>
                                                                </div>
                                                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                                                    <strong className="detail-label" style={{ display: 'inline-block', width: '100px', fontWeight: 'bold' }}>Address:</strong>
                                                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                                                        {quotationData?.project?.client?.address || ''}
                                                                    </span>
                                                                </div>
                                                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                                                    <strong className="detail-label" style={{ display: 'inline-block', width: '100px', fontWeight: 'bold' }}>Contact Phone:</strong>
                                                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                                                        {quotationData?.project?.client?.contacts?.[0]?.phone || ''}
                                                                    </span>
                                                                </div>
                                                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                                                    <strong className="detail-label" style={{ display: 'inline-block', width: '100px', fontWeight: 'bold' }}>Contact Email:</strong>
                                                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                                                        {quotationData?.project?.client?.contacts?.[0]?.email || ''}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Products Table */}
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '11px' }}>
                                                        <thead>
                                                            <tr>
                                                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Description</th>
                                                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>QTY</th>
                                                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Rate</th>
                                                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Amount</th>
                                                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>{vatRate * 100}% VAT</th>
                                                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Amount Inc. of VAT</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {quotationData?.product_item_details?.map((item, index) => {
                                                                const subtotal = Number(item.subtotal) || 0;
                                                                const vatAmount = Number(subtotal * vatRate);
                                                                const totalAmount = Number(subtotal + vatAmount);

                                                                return (
                                                                    <tr key={index}>
                                                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                                                                            <strong>{item.product?.product_name} :</strong>  {item.product?.description || item.item_description || ''}
                                                                        </td>
                                                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                                            {item.quantity || ''}
                                                                        </td>
                                                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                                            {Number(item.price || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                        </td>
                                                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                                            {subtotal.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                        </td>
                                                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                                            {vatAmount.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                        </td>
                                                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                                            {totalAmount.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                        <tfoot>
                                                            <tr style={{ backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
                                                                <td colSpan="3" style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Total:</td>
                                                                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                                    {quotationData?.product_item_details?.reduce((sum, item) => sum + Number(item.subtotal || 0), 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                </td>
                                                                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                                    {quotationData?.product_item_details?.reduce((sum, item) => sum + (Number(item.subtotal || 0) * vatRate), 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                </td>
                                                                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                                    {quotationData?.product_item_details?.reduce((sum, item) => sum + (Number(item.subtotal || 0) * (1 + vatRate)), 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                </td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>

                                                    {/* Amount Summary */}
                                                    <div style={{ width: '250px', marginLeft: 'auto', fontSize: '11px', marginTop: '10px' }}>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse', border: 'none' }}>
                                                            <tr>
                                                                <td style={{ padding: '4px 0', fontWeight: 'bold', textAlign: 'left', width: '60%', border: 'none' }}>
                                                                    Total Excl. Tax
                                                                </td>
                                                                <td style={{ padding: '4px 0', textAlign: 'right', border: 'none' }}>
                                                                    {basicAmountValue.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td style={{ padding: '4px 0', fontWeight: 'bold', textAlign: 'left', border: 'none' }}>
                                                                    Total VAT
                                                                </td>
                                                                <td style={{ padding: '4px 0', textAlign: 'right', border: 'none' }}>
                                                                    {vatAmountValue.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                </td>
                                                            </tr>
                                                            {discountAmountValue > 0 && (
                                                                <tr>
                                                                    <td style={{ padding: '4px 0', fontWeight: 'bold', textAlign: 'left', border: 'none' }}>
                                                                        Discount{createdInvoice.quotation?.discount_type === 'percentage' ? ' (%)' : ''}:
                                                                    </td>
                                                                    <td style={{ padding: '4px 0', textAlign: 'right', border: 'none' }}>
                                                                        -{discountAmountValue.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                    </td>
                                                                </tr>
                                                            )}
                                                            <tr>
                                                                <td style={{ padding: '6px 0', fontWeight: 'bold', textAlign: 'left', border: 'none' }}>Total Inc. Tax</td>
                                                                <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 'bold', border: 'none' }}>
                                                                    {afterDiscount.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                </td>
                                                            </tr>
                                                        </table>
                                                    </div>

                                                    {/* Amount in Words */}
                                                    <div style={{
                                                        marginTop: '20px',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '6px',
                                                        padding: '10px 15px',
                                                        background: '#fafafa',
                                                        fontSize: '13px',
                                                        color: '#000'
                                                    }}>
                                                        <strong>Amount (in Words):</strong> {generateAmountInWords(afterDiscount)}
                                                    </div>

                                                    {/* Terms & Conditions */}
                                                    {proformaInvoiceTerms && (
                                                        <div style={{ marginTop: '20px', padding: '10px 15px' }}>
                                                            <div style={{ fontSize: '12px', color: '#000', fontWeight: 'bold' }}>
                                                                Terms & Conditions:
                                                            </div>
                                                            <div
                                                                style={{ fontSize: '11px', lineHeight: '1.6', color: '#000', padding: '12px 18px' }}
                                                                dangerouslySetInnerHTML={{ __html: proformaInvoiceTerms }}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Bank Details */}
                                                    {matchedBanks.length > 0 && (
                                                        <div className="bank-details" style={{
                                                            marginTop: '20px',
                                                            border: '1px solid #ccc',
                                                            borderRadius: '6px',
                                                            padding: '12px',
                                                            background: '#fff'
                                                        }}>
                                                            <h4 style={{
                                                                margin: '0 0 10px 0',
                                                                fontSize: '13px',
                                                                color: '#000',
                                                                borderBottom: '1px solid #003366',
                                                                paddingBottom: '5px'
                                                            }}>
                                                                Bank Details
                                                            </h4>
                                                            {/* ... (keep your existing bank details JSX) */}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                /* EMPTY STATE - No Products and No Uploaded Document */
                                                <div className="text-center py-5">
                                                    <i className="fa fa-file-text fa-3x text-muted mb-3"></i>
                                                    <h5 className="text-muted">No Tax Invoice Content</h5>
                                                    <p className="text-muted mb-4">
                                                        This quotation doesn't have any products or custom integration details.
                                                    </p>
                                                    <Button
                                                        variant="primary"
                                                        onClick={handleShowUploadModal}
                                                    >
                                                        <i className="fa fa-upload me-2"></i>
                                                        Upload Tax Invoice Document
                                                    </Button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </Tab>
                            <Tab eventKey="uploaded" title={<span><i className="fa fa-cloud-upload-alt me-1"></i> Uploaded Document</span>}>
                                <div className="uploaded-tab-content">
                                    {hasUploadedDocument ? (
                                        <>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <div className="text-muted small">
                                                    <i className="fa fa-file-pdf me-1"></i> {createdInvoice.tax_invoice_file}
                                                </div>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => window.open(getFileUrl(), '_blank')}
                                                >
                                                    <i className="fa fa-download me-1"></i> Download Uploaded File
                                                </Button>
                                            </div>
                                            <div className="uploaded-document-view border rounded shadow-sm">
                                                <div style={{
                                                    height: '800px',
                                                    width: '100%',
                                                    overflow: 'hidden'
                                                }}>
                                                    <iframe
                                                        src={`${getFileUrl()}#toolbar=0&navpanes=0`}
                                                        width="100%"
                                                        height="100%"
                                                        style={{ border: 'none', display: 'block' }}
                                                        title="Tax Invoice Document"
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center py-5 bg-light border rounded">
                                            <i className="fa fa-upload fa-3x text-muted mb-3"></i>
                                            <h5>No Document Uploaded</h5>
                                            <p className="text-muted">You haven't uploaded a signed tax invoice yet.</p>
                                            <Button variant="primary" onClick={handleShowUploadModal}>
                                                Upload Document Now
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </Tab>
                        </Tabs>
                    </div>
                </Offcanvas.Body>

                <div className="p-4 d-flex justify-content-end border-top bg-light">
                    <Button
                        variant="outline-secondary"
                        onClick={handleClose}
                        className="me-2"
                        size="sm"
                    >
                        Close
                    </Button>

                    {/* Dynamic action buttons based on view mode */}

                </div>
            </Offcanvas>

            {/* Upload Modal */}
            <Offcanvas show={showUploadModal} onHide={handleCloseUploadModal} placement='end'>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>
                        {hasUploadedDocument ? 'Update Tax Invoice Document' : 'Upload Tax Invoice Document'}
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {uploadError && (
                        <Alert variant="danger" className="mb-3">
                            {uploadError}
                        </Alert>
                    )}

                    <Form.Group>
                        <Form.Label>Tax Invoice Document *</Form.Label>
                        <Form.Control
                            type="file"
                            onChange={handleFileChange}
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        />
                        <Form.Text className="text-muted">
                            Supported formats: PDF, Word, JPEG, PNG (Max: 5MB)
                        </Form.Text>
                    </Form.Group>

                    {hasUploadedDocument && (
                        <div className="mt-3 p-3 border rounded bg-light">
                            <h6 className="mb-2">Current Document:</h6>
                            <div className="d-flex justify-content-between align-items-center">
                                <span>{createdInvoice.tax_invoice_file}</span>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => window.open(getFileUrl(), '_blank')}
                                >
                                    <i className="fa fa-eye me-1"></i> View
                                </Button>
                            </div>
                        </div>
                    )}
                </Offcanvas.Body>
                <div className="p-3 border-top d-flex justify-content-end bg-light">
                    <Button variant="secondary" onClick={handleCloseUploadModal}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleUpload}
                        disabled={uploading || !uploadFile}
                    >
                        {uploading ? 'Uploading...' : (hasUploadedDocument ? 'Update Document' : 'Upload Document')}
                    </Button>
                </div>
            </Offcanvas>
        </>
    );
};

export default TaxInvoicePreview;